<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	//Services
	import { format } from 'svelte-i18n';
	import { postReq } from '../../../services/http';
	import { hapticFeedback } from '../../../services/native_container_requests_ios';
	//Hooks
	import { beforeUpdate } from 'svelte';
	//Helpers
	import {
		input_status_check,
		is_gen_z,
		get_z_wave_channel_structure_from_category,
		real_id,
		real_id_z,
	} from '../../../helpers/interface_helpers';
	import { unlockPin } from '../../../helpers/pin_lock_helpers';
	import { capFirstChar } from '../../../helpers/utilities';
	import { calculate_timer } from '../../../helpers/timer_helpers';
	//Stores
	import { auth_locked_devices } from 'src/stores/device-runtime/local-auth';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { os } from 'src/stores/session/native-bridge';
	//Design system components
	import {
		Text,
		DeviceButton,
		toast,
		DeviceButtonV2,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import { pin_unlocked_devices } from 'src/stores/device-runtime/pin-unlocked';

	//App components
	import PinUnlock from '../../../components/PinUnlock.svelte';
	import { hasControlRights } from 'src/helpers/user_helpers';

	let className = '';
	export { className as class };

	export let status,
		device,
		settings,
		isWide = false;
	export let useV2Components = false;
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {
		size: 's',
	};

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device.id);
	$: settings = $settingsStore;

	let isLoading = false;
	let ison = false;
	let expected_ison = false;
	const isGenZ = is_gen_z(device);

	const component = get_z_wave_channel_structure_from_category(device);

	let hasTimer = false;
	let timer = { time: undefined, percentage: undefined };
	let timerStarted;
	let cacheTimerStarted;
	let tagline = '';

	let pinLocked;
	let showModal = false;
	let inputState: 'on' | 'off';

	$: hasRights = hasControlRights(device);

	beforeUpdate(() => {
		pinLocked =
			typeof $pin_unlocked_devices[device.id] === 'undefined' && $devices[device.id]?.pin_lock;

		// timer status handles
		if (status) {
			// console.warn('checking status', status);
			if (typeof status?.[component] !== 'undefined') {
				ison = status?.[component]?.currentValue;
				hasTimer = status?.[component]?.duration?.value !== 0;
			}

			tagline = ison
				? $format('label_action_turn_off', { default: 'turn off' })
				: $format('label_action_turn_on', { default: 'turn on' });

			timerStarted = hasTimer ? status?.[component]?.duration?.value : false;
			if (typeof cacheTimerStarted === 'undefined') {
				cacheTimerStarted = timerStarted;
			}
			if (timerStarted && hasTimer) {
				let [_, _time, _percentage, _ison] = calculate_timer(device, status, settings);
				if (Object.values(timer).some((t) => typeof t === 'undefined')) {
					timer.time = _time;
					timer.percentage = Number(_percentage.toFixed(0));
				}
				if (cacheTimerStarted !== timerStarted) {
					cacheTimerStarted = timerStarted;
					timer.time = _time;
					timer.percentage = Number(_percentage.toFixed(0));
				}
			} else {
				timer.time = undefined;
				timer.percentage = undefined;
			}
			if ($devices[device.id]?.input_state) {
				inputState = input_status_check(status, device) ? 'on' : 'off';
			}
		}
	});

	const handleTurn = async () => {
		if (!pinLocked) {
			if ($os === 'IOS') {
				hapticFeedback('buttonPressed');
			}
			if (isLoading) return;
			isLoading = true;

			let res;

			expected_ison = !ison;

			res = await postReq(`/device/${device.category}/control`, {
				id: real_id_z(device.id),
				channel: device.channel,
				turn: ison ? 'off' : 'on',
			});

			if (res.isok) {
				// if (ison !== expected_ison) {
				ison = expected_ison;
				status[component].currentValue = expected_ison;

				devices_status.update_device_status({
					id: real_id_z(device.id),
					msg: status,
				});
				// }
			} else if ($auth_locked_devices.indexOf(real_id_z(device.id)) == -1) {
				toast.open({ type: 'danger', title: $format('error_global') });
			}
			isLoading = false;
		} else {
			showModal = true;
		}
	};

	const handlePin = () => {
		pinLocked = unlockPin(device, pinLocked);
		handleTurn();
	};

	const a11yProps = { timerAriaLabel: $format('label_turn_device', { default: 'Turn device' }) };
</script>

{#key timerStarted || hasTimer || ison}
	{#if useV2Components}
		<DeviceButtonV2
			id="devicebutton-6fc6j89"
			{isWide}
			{isLoading}
			{hasTimer}
			{timer}
			indicator={inputState}
			isActive={ison}
			icon="fa fa-power-off"
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			variant="primary"
			on:click={(e) => {
				e.stopPropagation();
				handleTurn();
			}}
			disabled={!status || !hasRights}
			class={className}
			{...deviceButtonV2Props}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-6fc6j89"
			{isWide}
			{isLoading}
			{hasTimer}
			{timer}
			indicator={inputState}
			isActive={ison}
			size={isWide ? 'l' : 'm'}
			icon="fa fa-power-off"
			aria-label={$format('label_turn_device', { default: 'Turn device' })}
			{a11yProps}
			variant="primary"
			on:click={(e) => {
				e.stopPropagation();
				handleTurn();
			}}
			disabled={!status || !hasRights}
			class={className}
		/>
	{/if}
{/key}
{#if isWide}
	<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
{/if}

{#if showModal}
	<PinUnlock bind:showModal on:unlock={handlePin} on:cancel={() => (showModal = false)} />
{/if}
